import { useEffect, useRef, useState } from 'react';
import '../styles/cinematic.css';

/*
 * The cinematic replacement for the sliding image strip.
 *
 * A slide used to push the last one out sideways. Here each picture dissolves
 * into the next while it drifts very slowly, the way a film cuts between
 * establishing shots, and a thin progress bar under the caption shows how long
 * is left on the current one. It only plays while on screen, holds still while
 * the pointer is on it, and stays a still picture for anybody who has asked
 * their device for less motion.
 *
 * Two shapes. 'film' is a wide 21:9 frame with the caption over the picture,
 * for photographs. 'screen' is a 16:10 frame that never crops, with the caption
 * below it, for product screenshots, which are information to their last row.
 *
 * A slide whose file is missing is dropped, so a picture that has not been
 * uploaded yet never leaves a hole in the reel.
 */
export interface ReelSlide { src: string; alt: string; caption?: string }

function useReduced(): boolean {
  const [r, setR] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setR(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return r;
}

export function CinematicReel({ slides, shape = 'film', ms = 6000 }: {
  slides: ReelSlide[]; shape?: 'film' | 'screen'; ms?: number;
}) {
  const reduced = useReduced();
  const root = useRef<HTMLDivElement>(null);
  const [bad, setBad] = useState<Record<string, true>>({});
  const [i, setI] = useState(0);
  const [inView, setInView] = useState(false);
  const [held, setHeld] = useState(false);
  const list = slides.filter((s) => !bad[s.src]);
  const n = list.length;
  const at = n ? i % n : 0;
  const playing = inView && !held && !reduced && n > 1;

  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => setInView(!!e?.isIntersecting), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!playing) return;
    const t = window.setTimeout(() => setI((x) => x + 1), ms);
    return () => window.clearTimeout(t);
  }, [playing, at, ms]);

  if (!n) return null;
  const cur = list[at]!;

  return (
    <div className={`cr cr-${shape}`} ref={root}
      onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)} onBlur={() => setHeld(false)}>
      <div className="cr-stage" role="group" aria-roledescription="carousel" aria-label="Pictures">
        {slides.map((s, k) => {
          const idx = list.findIndex((x) => x.src === s.src);
          if (idx < 0) return null;
          const on = idx === at;
          return (
            <img key={s.src} src={s.src} alt={on ? s.alt : ''} aria-hidden={!on}
              className={`cr-img${on ? ' on' : ''}${k % 2 ? ' alt' : ''}${playing ? ' go' : ''}`}
              style={{ ['--ms' as string]: `${ms + 1400}ms` }}
              loading="eager" decoding="async"
              onError={() => setBad((b) => ({ ...b, [s.src]: true }))} />
          );
        })}
        {shape === 'film' && <span className="cr-bar cr-bar-top" aria-hidden />}
        {shape === 'film' && <span className="cr-bar cr-bar-bot" aria-hidden />}
        {shape === 'film' && cur.caption && <p className="cr-cap" key={cur.src} aria-live="polite">{cur.caption}</p>}
        {n > 1 && (
          <div className="cr-steps" role="tablist" aria-label="Choose a picture">
            {list.map((s, k) => (
              <button key={s.src} type="button" role="tab" aria-selected={k === at} aria-label={`Picture ${k + 1} of ${n}`}
                className={`cr-step${k === at ? ' on' : ''}${k < at ? ' done' : ''}`} onClick={() => setI(k)}>
                <i style={playing && k === at ? { animationDuration: `${ms}ms` } : undefined} />
              </button>
            ))}
          </div>
        )}
      </div>
      {shape === 'screen' && cur.caption && (
        <p className="cr-below" aria-live="polite"><span>{at + 1}/{n}</span>{cur.caption}</p>
      )}
    </div>
  );
}
